import type { ShopScope } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

interface ShopScopePickerProps {
  value: ShopScope;
  onChange: (next: ShopScope) => void;
  error?: string | null;
  className?: string;
}

/**
 * 资源/店铺范围选择(ADP-167,对应 Xnurta「资源」区)。
 * 全部店铺(随工作区店铺变化)或指定店铺勾选。
 */
export function ShopScopePicker({ value, onChange, error, className }: ShopScopePickerProps) {
  const { t } = useTranslation("account");
  const shops = useAuthStore((state) => state.shops);
  const selectedIds = value.mode === "selected" ? value.shopIds : [];

  function toggleShop(shopId: string, checked: boolean) {
    const next = checked
      ? [...selectedIds, shopId]
      : selectedIds.filter((existing) => existing !== shopId);
    onChange({ mode: "selected", shopIds: next });
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className={cn("rounded-md border p-3", error ? "border-destructive" : "border-input")}>
        <div className="flex items-center gap-4 pb-2">
          <Label className="flex items-center gap-2 font-normal">
            <input
              type="radio"
              name="shop-scope-mode"
              checked={value.mode === "all"}
              onChange={() => onChange({ mode: "all" })}
              className="accent-primary"
            />
            <span className="text-sm">{t("drawer.shopScopeAll")}</span>
          </Label>
          <Label className="flex items-center gap-2 font-normal">
            <input
              type="radio"
              name="shop-scope-mode"
              checked={value.mode === "selected"}
              onChange={() => onChange({ mode: "selected", shopIds: selectedIds })}
              className="accent-primary"
            />
            <span className="text-sm">{t("drawer.shopScopeSelected")}</span>
          </Label>
        </div>
        {value.mode === "selected" ? (
          <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto pl-6">
            {shops.map((shop) => (
              <li key={shop.shopId}>
                <Label className="flex items-center gap-2 font-normal">
                  <Checkbox
                    checked={selectedIds.includes(shop.shopId)}
                    onCheckedChange={(checked) => toggleShop(shop.shopId, checked === true)}
                    aria-label={shop.shopName}
                  />
                  <span className="text-sm">{shop.shopName}</span>
                </Label>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {error ? <p className="text-xs text-destructive-foreground">{error}</p> : null}
    </div>
  );
}
